/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, July 21st 2024, 3:41:59 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { useCommonStore } from "common/store";
import { useAPIRequest, APISERVICEPATHS } from "lib/api";
import { TLocales, TranslatableModel, TranslatableContent } from "lib/schemas";

const useGetLanguages = () => {
  const { locales, populateLocales } = useCommonStore();
  const { makeRequest, makeRequestCleanUp, ...rest } = useAPIRequest<TLocales>({
    apiServicePath: `${APISERVICEPATHS.translationLocales}`,
    makeRequestOnInitialMount: false,
  });

  const getSupportedLocales = () => {
    if (Object.keys(locales).length) return;

    makeRequest({ setValidAPIResponse: populateLocales, apiQueryParams: { supported: true } });
  };

  return { getSupportedLocales, locales, ...rest };
};

const useGetTranslatableModels = () => {
  const { makeRequest, makeRequestCleanUp, ...rest } = useAPIRequest<TranslatableModel[]>({
    apiServicePath: `${APISERVICEPATHS.translatableModels}`,
    makeRequestOnInitialMount: false,
  });

  const getModels = (onFetch: (models: TranslatableModel[]) => void) => {
    makeRequest({ setValidAPIResponse: onFetch });
  };

  return { getModels, ...rest };
};

const usePatchTranslationText = () => {
  const { makeRequest, makeRequestCleanUp, ...rest } = useAPIRequest<Partial<TranslatableContent>>({
    apiServicePath: `${APISERVICEPATHS.manageTranslations}`,
    makeRequestOnInitialMount: false,
  });

  const patchTranslationText = (
    modelId: string,
    sourceTextId: string,
    data: Record<string, string>,
    onPatch?: (resp: any) => void
  ) => {
    makeRequest({
      setValidAPIResponse: onPatch,
      method: "PATCH",
      apiPathParams: [modelId, sourceTextId],
      body: JSON.stringify({ i18n: data }),
    });
  };

  return { patchTranslationText, ...rest };
};

const usePOFileAPI = () => {
  const { makeRequest, makeRequestCleanUp, ...rest } = useAPIRequest<any>({
    apiServicePath: `${APISERVICEPATHS.translatableModelsPOFile}`,
    makeRequestOnInitialMount: false,
  });

  const exportFile = (
    targetLanguageCode: string,
    onExport: (fileBlob: Blob) => void,
    model_identifier?: TranslatableModel["id"]
  ) => {
    if (!targetLanguageCode) return;

    makeRequest({
      apiPathParams: [targetLanguageCode],
      apiQueryParams: model_identifier ? { model_identifier } : null,
      setValidAPIResponse: onExport,
      responseIsFile: true,
    });
  };

  const importFile = (
    file: File,
    targetLanguageCode: string,
    onImport: (importedTranslations: Record<string, string>) => void,
    model_identifier?: TranslatableModel["id"]
  ) => {
    if (!targetLanguageCode) return;
    const formData = new FormData();
    formData.append("po_file", file);

    makeRequest({
      method: "POST",
      headers: {}, // Let the browser auto set the content type as multipart once it sees there is a file.
      body: formData,
      apiPathParams: [targetLanguageCode],
      apiQueryParams: model_identifier ? { model_identifier } : null,
      setValidAPIResponse: onImport,
    });
  };

  return { exportFile, importFile, ...rest };
};

export const useTranslationAPI = () => ({
  useGetLanguages,
  useGetTranslatableModels,
  usePatchTranslationText,
  usePOFileAPI,
});
